import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectMedSummaryAssetForMatter } from '@/medical-billing-summaries/asset/store/selectors'
import MedSummaryAssetDownloadButton from '@/medical-billing-summaries/asset/components/MedSummaryAssetDownloadButton'
import { matterPaths } from '@/routing/paths'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'

/**
 * Breadcrumbs for the living Medical Billing Summary: Matter > Medical Billing Summary,
 * with the Download action once the summary has entries. Sub-pages (Add Files, Previous
 * summaries) add their own crumb and make the summary crumb a clickable parent.
 */
export default function MatterBreadcrumbsMedSummaryAsset({ subPageCrumb }: { subPageCrumb?: string } = {}) {
  const navigate = useNavigate()

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: activeMatterId }))
  const asset = useAppSelector((state: RootState) => selectMedSummaryAssetForMatter(state, { matterId: activeMatterId ?? undefined }))

  const breadcrumbItems: BreadcrumbItemType[] = useMemo(() => {
    if (!initialLoadComplete) return []

    const items: BreadcrumbItemType[] = []
    if (matter) {
      items.push({
        id: `matter-${matter.id}`,
        label: matter.name,
        onClick: () => navigate(matterPaths.overview(matter.id)),
      })
    }
    items.push({
      id: 'med-summary-asset',
      label: 'Medical Billing Summary',
      // On sub-pages, Medical Billing Summary becomes a clickable parent crumb.
      ...(subPageCrumb && activeMatterId ? { onClick: () => navigate(matterPaths.billing.index(activeMatterId)) } : {}),
    })
    if (subPageCrumb) {
      items.push({ id: 'sub-page', label: subPageCrumb })
    }
    return items
  }, [initialLoadComplete, matter, navigate, subPageCrumb, activeMatterId])

  const showActions = asset && asset.total_entries > 0 && activeMatterId

  return (
    // Negative margin is to make this match the design files / chat (due to sidebar closing button gap)
    <div className="h-[52px] lg:h-[60px] flex lg:-ml-3">
      <div className="w-full px-3 lg:pr-6 flex justify-between items-center border-b-[0.5px] bg-brand-neutral-100 lg:bg-brand-neutral-50">
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumbs items={breadcrumbItems} maxLabelLength={30} maxActiveLabelLength={60} persistFirstItem />
        </div>

        {showActions && (
          <div className="hidden lg:flex items-center justify-center gap-3.5">
            <MedSummaryAssetDownloadButton assetId={asset.id} />
          </div>
        )}
      </div>
    </div>
  )
}
